import React, { memo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  deviceSetupLoading,
  deviceSetupLogin,
  logInLabel,
} from '../../strings';

import { useAuth } from '../../context/Auth';
import Placeholder from '../placeholder/Placeholder';
import PrimaryButton from '../primaryButton/PrimaryButton';
import Layout, { Props as LayoutProps } from '../../pages/layout/Layout';

import styles from './FlowStepper.module.css';

export type Props = {
  layoutPropsArray: LayoutProps[];
  onPageZeroBack: () => void;
};

const FlowStepper = ({ layoutPropsArray, onPageZeroBack }: Props) => {
  const { authenticated, isReady, login } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageSearchparam = searchParams.get('page');
  const pageInt = pageSearchparam ? parseInt(pageSearchparam, 10) : 0;
  const pageIndex = Math.max(0, Math.min(pageInt, layoutPropsArray.length - 1));

  useEffect(() => {
    if (!pageSearchparam) {
      const next = new URLSearchParams(searchParams);
      next.set('page', '0');
      setSearchParams(next);
    }
  }, [pageSearchparam, searchParams, setSearchParams]);

  const goToPage = (n: number) => {
    if (n !== pageIndex) {
      const next = new URLSearchParams(searchParams);
      next.set('page', n.toString());
      setSearchParams(next);
    }
  };

  const render = () => {
    if (!isReady) {
      return <Placeholder isLoading message={deviceSetupLoading} />;
    }

    if (!authenticated) {
      return (
        <>
          <div className={styles.description}>
            {deviceSetupLogin}
            <PrimaryButton onClick={() => login()} unstyled>
              {logInLabel}
            </PrimaryButton>
          </div>
        </>
      );
    }

    const layoutProps = layoutPropsArray[pageIndex];

    return (
      <Layout
        {...layoutProps}
        showSkip={false}
        backButton={{
          ...layoutProps.backButton,
          onClick: (e) => {
            const onClick = layoutProps.backButton?.onClick;
            onClick && onClick(e);

            if (pageIndex === 0) {
              onPageZeroBack();
            } else {
              goToPage(pageIndex - 1);
            }
          },
        }}
        nextButton={{
          ...layoutProps.nextButton,
          onClick: (e) => {
            const onClick = layoutProps.nextButton?.onClick;
            onClick && onClick(e);

            if (pageIndex + 1 !== layoutPropsArray.length) {
              goToPage(pageIndex + 1);
            }
          },
        }}
      />
    );
  };
  return render();
};

export default memo(FlowStepper);
